import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Alert, User, Operation, UserRole } from '@vigilo/shared';
import { useMockMode } from './MockModeContext';
import { AUTH_CLEARED_EVENT, clearAuthStorage, get, post, TOKEN_KEY, USER_KEY } from '@vigilo/api-client';
import { MOCK_OPERATIONS } from '../data/mock';
import { useAlertData } from '../app/data/useMockData';
import { CROWD_OPERATIONS_CHANGED_EVENT } from '../app/hooks/useOperations';
import { CROWD_SIMULATION_RESET_EVENT } from '../app/hooks/useSimulation';
import {
  chooseDefaultOperation,
  findSeededDefaultOperation,
  sortOperationsForSession,
} from '../app/utils/default-operation';
import type { AlertStreamConnectionStatus, AlertStreamSource, AlertStreamSummary } from '../app/hooks/useAlertStream';
import { EMPTY_ALERT_STREAM_SUMMARY } from '../app/hooks/useAlertStream';
import { Icons } from '@vigilo/design-system';

export interface NavItem {
  id: string;
  label: string;
  icon: keyof typeof Icons;
  path: string;
  description?: string;
  badgeCount?: number;
  requiredRole?: UserRole;
}

export interface NavGroup {
  id: string;
  label: string;
  items: NavItem[];
}

export const CROWD_MONITORING_NAV: NavGroup[] = [
  {
    id: 'operations',
    label: 'OPERATIONS',
    items: [
      { id: 'operations', label: 'Operations', icon: 'Calendar', path: '/operations' },
      { id: 'command', label: 'Command Dashboard', icon: 'Grid', path: '/command', requiredRole: 'command_supervisor' },
      { id: 'map', label: 'Map Dashboard', icon: 'Map', path: '/map' },
      { id: 'zones', label: 'Zones', icon: 'Zones', path: '/zones' },
      { id: 'alerts', label: 'Alerts', icon: 'Alert', path: '/alerts', badgeCount: 0 },
    ],
  },
  {
    id: 'analysis',
    label: 'ANALYSIS',
    items: [
      { id: 'movement', label: 'Movement', icon: 'Activity', path: '/movement' },
      { id: 'flags', label: 'Flagged Tokens', icon: 'Flag', path: '/flags' },
      { id: 'reports', label: 'Reporting', icon: 'Report', path: '/reports' },
    ],
  },
  {
    id: 'admin',
    label: 'ADMIN',
    items: [
      { id: 'admin', label: 'Administration', icon: 'Settings', path: '/admin', requiredRole: 'system_admin' },
    ],
  },
];

function crowdMonitoringNav(activeAlertCount: number): NavGroup[] {
  return CROWD_MONITORING_NAV.map(group => ({
    ...group,
    items: group.items.map(item => (
      item.id === 'alerts'
        ? { ...item, badgeCount: activeAlertCount }
        : { ...item }
    )),
  }));
}

export interface AppState {
  currentUser:            User;
  setCurrentUser:         (user: User | null) => void;
  navConfig:              NavGroup[];
  setNavConfig:           (config: NavGroup[]) => void;
  activeOperations:       Operation[];
  selectedOperation:      Operation | null;
  /** False until the first operations fetch has settled, so route guards can wait for the restore instead of redirecting. */
  operationsResolved:     boolean;
  setSelectedOperation:   (op: Operation | null) => void;
  addActiveOperation:     (op: Operation) => void;
  removeActiveOperation:  (id: string) => void;
  refreshOperations:      () => Promise<void>;
  activeAlertCount:       number;
  setActiveAlertCount:    (n: number) => void;
  selectedOperationAlerts: Alert[];
  selectedOperationAlertSummary: AlertStreamSummary;
  alertStreamConnected:   boolean;
  alertStreamConnectionStatus: AlertStreamConnectionStatus;
  alertStreamSource:      AlertStreamSource;
  alertStreamLastEventAt: string | null;
  alertStreamLastSyncAt:  string | null;
  alertStreamError:       string | null;
  acknowledgeAlert:       (alertId: string) => Promise<void>;
  escalateAlert:          (alertId: string) => Promise<void>;
  resolveAlert:           (alertId: string, note: string) => Promise<void>;
  theme:                  'dark' | 'light';
  toggleTheme:            () => void;
  // Operation creation modal
  isOperationModalOpen:   boolean;
  operationModalVenueId:  string | null;
  openOperationModal:     (venueId?: string) => void;
  closeOperationModal:    () => void;
  // Operation close modal
  isCloseModalOpen:       boolean;
  openCloseModal:         () => void;
  closeCloseModal:        () => void;
  logout:                 () => Promise<void>;
}

const AppCtx = createContext<AppState | null>(null);

export function useApp(): AppState {
  const ctx = useContext(AppCtx);
  if (!ctx) throw new Error('useApp must be used inside <AppProvider>');
  return ctx;
}

const MOCK_USER: User = {
  id:          'mock-user-001',
  email:       'analyst@vigilo.io',
  displayName: 'S. Reyes',
  role:        'crowd_analyst',
  team:        'Alpha Team',
  orgId:       'org-vigilo',
  active:      true,
  lastSeen:    null,
};

const SELECTED_OPERATION_KEY = 'vigilo_selected_operation_id';
const ACTIVE_OPERATION_STATUSES = new Set(['active', 'paused']);
const OPERATION_EXPIRY_REFRESH_BUFFER_MS = 1_000;
const OPERATION_EXPIRY_REFRESH_MAX_DELAY_MS = 60_000;

function loadStoredUser(): User {
  try {
    const raw = localStorage.getItem(USER_KEY());
    if (raw) return JSON.parse(raw) as User;
  } catch { /* corrupt entry — fall through */ }
  return MOCK_USER;
}

function readAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY());
}

function unwrapOps(body: unknown): Operation[] {
  if (Array.isArray(body)) return body as Operation[];
  const b = body as { data?: Operation[] };
  return b.data ?? [];
}

function persistSelectedOperation(op: Operation | null) {
  if (op) {
    sessionStorage.setItem(SELECTED_OPERATION_KEY, op.id);
  } else {
    sessionStorage.removeItem(SELECTED_OPERATION_KEY);
  }
}

function activeMockOperations(): Operation[] {
  return MOCK_OPERATIONS.filter(op => ACTIVE_OPERATION_STATUSES.has(op.status));
}

const OPERATION_RUN_WATCH_MS = 10_000;

export function AppProvider({ children }: { children: ReactNode }) {
  const { isMock } = useMockMode();
  const navigate = useNavigate();

  const [theme, setTheme]                = useState<'dark' | 'light'>('dark');
  const [currentUser, setUserState]      = useState<User>(loadStoredUser);
  const [authToken, setAuthToken]        = useState<string | null>(readAuthToken);
  const [activeAlertCount, setAlertCnt]  = useState(0);
  const [navConfigOverride, setNavConfigOverride] = useState<NavGroup[] | null>(null);
  const [activeOperations, setActiveOps] = useState<Operation[]>([]);
  const [selectedOperation, setSelectedOp] = useState<Operation | null>(null);
  const [operationsResolved, setOperationsResolved] = useState(false);
  const selectedOperationIdRef = useRef<string | null>(null);
  const selectedOperationRef = useRef<Operation | null>(null);
  const operationAlertState = useAlertData(selectedOperation?.id ?? null, {
    enabled: isMock || Boolean(authToken),
    requireOperation: true,
    zoneIds: selectedOperation?.assignedZoneIds ?? [],
  });

  // Operation creation modal state
  const [isOperationModalOpen,  setIsOpModalOpen]  = useState(false);
  const [operationModalVenueId, setOpModalVenueId] = useState<string | null>(null);

  // Operation close modal state
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);

  useEffect(() => {
    selectedOperationIdRef.current = selectedOperation?.id ?? null;
    selectedOperationRef.current = selectedOperation;
  }, [selectedOperation]);

  const selectedOperationAlerts = selectedOperation
    ? operationAlertState.alerts.filter(alert => (
      alert.operationId === selectedOperation.id
      && selectedOperation.assignedZoneIds.includes(alert.zoneId)
    ))
    : [];
  const selectedOperationActiveAlertCount = selectedOperationAlerts.filter(
    alert => alert.status !== 'resolved',
  ).length;
  const selectedOperationAlertSummary = selectedOperation
    ? operationAlertState.summary
    : EMPTY_ALERT_STREAM_SUMMARY;
  const selectedOperationFullActiveAlertCount = selectedOperation
    ? selectedOperationAlertSummary.active
    : selectedOperationActiveAlertCount;

  useEffect(() => {
    setAlertCnt(selectedOperationFullActiveAlertCount);
  }, [selectedOperationFullActiveAlertCount]);

  useEffect(() => {
    function handleStorage(e: StorageEvent) {
      if (e.key === TOKEN_KEY()) setAuthToken(e.newValue);
    }
    function handleAuthCleared() {
      setAuthToken(null);
      setUserState(MOCK_USER);
      setActiveOps([]);
      setSelectedOp(null);
    }
    window.addEventListener('storage', handleStorage);
    window.addEventListener(AUTH_CLEARED_EVENT, handleAuthCleared);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener(AUTH_CLEARED_EVENT, handleAuthCleared);
    };
  }, []);

  function setCurrentUser(user: User | null) {
    if (user) {
      localStorage.setItem(USER_KEY(), JSON.stringify(user));
      setUserState(user);
    } else {
      localStorage.removeItem(USER_KEY());
      setUserState(MOCK_USER);
    }
    setAuthToken(readAuthToken());
  }

  const logout = useCallback(async (): Promise<void> => {
    try {
      await post('/auth/logout');
    } catch (err) {
      console.warn('[AppContext] logout request failed; clearing local session anyway:', err);
    } finally {
      clearAuthStorage();
      setCurrentUser(null);
      setAuthToken(null);
      setActiveOps([]);
      setSelectedOp(null);
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  function setSelectedOperation(op: Operation | null) {
    persistSelectedOperation(op);
    setSelectedOp(op);
  }

  const restoreSelectedOperation = useCallback((ops: Operation[], preferredId?: string | null) => {
    if (preferredId) {
      const preferred = ops.find(op => op.id === preferredId);
      if (preferred) {
        persistSelectedOperation(preferred);
        setSelectedOp(preferred);
        return;
      }
    }

    // A selection made earlier in this browser session wins; the seeded default is only for fresh sessions.
    const persistedId = sessionStorage.getItem(SELECTED_OPERATION_KEY);
    if (persistedId) {
      const persisted = ops.find(op => op.id === persistedId);
      if (persisted) {
        persistSelectedOperation(persisted);
        setSelectedOp(persisted);
        return;
      }
      sessionStorage.removeItem(SELECTED_OPERATION_KEY);
    }

    const seededDefault = findSeededDefaultOperation(ops);
    if (seededDefault) {
      persistSelectedOperation(seededDefault);
      setSelectedOp(seededDefault);
      return;
    }

    const fallback = chooseDefaultOperation(ops);
    if (fallback) {
      persistSelectedOperation(fallback);
      setSelectedOp(fallback);
      return;
    }

    persistSelectedOperation(null);
    setSelectedOp(null);
  }, []);

  // Adds or replaces an operation in the active list; auto-selects if first.
  function addActiveOperation(op: Operation) {
    setActiveOps(prev => {
      const filtered = prev.filter(p => p.id !== op.id);
      return [op, ...filtered];
    });
    setSelectedOp(prev => {
      if (prev) return prev;
      persistSelectedOperation(op);
      return op;
    });
  }

  // Removes an operation from the active list; clears selection if it was selected.
  function removeActiveOperation(id: string) {
    setActiveOps(prev => prev.filter(p => p.id !== id));
    setSelectedOp(prev => {
      if (prev?.id !== id) return prev;
      persistSelectedOperation(null);
      return null;
    });
  }

  const refreshOperations = useCallback(async (): Promise<void> => {
    if (isMock) return;
    if (!authToken) return;
    try {
      const body = await get<Operation[]>('/operations/active');
      const ops = sortOperationsForSession(unwrapOps(body));
      setActiveOps(ops);
      const currentId = selectedOperationIdRef.current;
      if (currentId) {
        const stillActive = ops.find(op => op.id === currentId);
        if (stillActive) {
          persistSelectedOperation(stillActive);
          setSelectedOp(stillActive);
          return;
        }
      }
      restoreSelectedOperation(ops);
    } catch (err) {
      console.warn('[AppContext] refreshOperations failed:', err);
    }
  }, [authToken, isMock, restoreSelectedOperation]);

  // Detect a new run of the selected operation (see refreshOperations) without a page reload.
  useEffect(() => {
    if (isMock || !authToken || !selectedOperation?.id) return;
    let cancelled = false;
    const timer = window.setInterval(() => {
      get<Operation[]>('/operations/active')
        .then(body => {
          if (cancelled) return;
          const current = selectedOperationRef.current;
          const latest = unwrapOps(body).find(op => op.id === current?.id);
          if (!current || !latest) return;
          // A new run of the same operation (e.g. the demo rolled over) restarts actualStart. Streams that
          // only receive deltas would otherwise keep showing the previous run's alerts and flags.
          if (current.actualStart && latest.actualStart && current.actualStart !== latest.actualStart) {
            window.dispatchEvent(new Event(CROWD_SIMULATION_RESET_EVENT));
            void refreshOperations();
          }
        })
        .catch(() => { /* transient; the next tick retries */ });
    }, OPERATION_RUN_WATCH_MS);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [isMock, authToken, selectedOperation?.id, refreshOperations]);

  // Initial load
  useEffect(() => {
    if (isMock) {
      const active = sortOperationsForSession(activeMockOperations());
      setActiveOps(active);
      restoreSelectedOperation(active, selectedOperationIdRef.current);
      setOperationsResolved(true);
      return;
    }
    if (!authToken) {
      setOperationsResolved(true);
      return;
    }
    setOperationsResolved(false);
    get<Operation[]>('/operations/active')
      .then(body => {
        const ops = sortOperationsForSession(unwrapOps(body));
        setActiveOps(ops);
        restoreSelectedOperation(ops, selectedOperationIdRef.current);
      })
      .catch(err => {
        console.warn('[AppContext] Failed to fetch active operations:', err);
      })
      .finally(() => setOperationsResolved(true));
  }, [authToken, isMock, restoreSelectedOperation]);

  useEffect(() => {
    if (isMock || !authToken || activeOperations.length === 0) return;

    // Only ends that are still ahead need a refresh. An operation that is past its scheduled end but
    // still active (the server does not auto-close it) would otherwise re-arm this timer every second,
    // refetching the list and re-rendering the whole app for as long as it stays open.
    const nowMs = Date.now();
    const nextEndMs = activeOperations
      .filter(op => ACTIVE_OPERATION_STATUSES.has(op.status))
      .map(op => new Date(op.scheduledEnd).getTime())
      .filter(ms => Number.isFinite(ms) && ms > nowMs)
      .sort((a, b) => a - b)[0];

    if (!nextEndMs) return;

    const delay = Math.min(
      Math.max(0, nextEndMs - Date.now()) + OPERATION_EXPIRY_REFRESH_BUFFER_MS,
      OPERATION_EXPIRY_REFRESH_MAX_DELAY_MS,
    );
    const timer = window.setTimeout(() => {
      void refreshOperations();
    }, delay);

    return () => window.clearTimeout(timer);
  }, [activeOperations, authToken, isMock, refreshOperations]);

  useEffect(() => {
    if (isMock || !authToken) return;
    function handleOperationsChanged() {
      void refreshOperations();
    }
    window.addEventListener(CROWD_OPERATIONS_CHANGED_EVENT, handleOperationsChanged);
    return () => window.removeEventListener(CROWD_OPERATIONS_CHANGED_EVENT, handleOperationsChanged);
  }, [authToken, isMock, refreshOperations]);

  const openOperationModal = useCallback((venueId?: string) => {
    // May be passed directly to onClick; React supplies a MouseEvent there.
    setOpModalVenueId(typeof venueId === 'string' ? venueId : null);
    setIsOpModalOpen(true);
  }, []);

  const closeOperationModal = useCallback(() => {
    setIsOpModalOpen(false);
    setOpModalVenueId(null);
  }, []);

  const openCloseModal  = useCallback(() => setIsCloseModalOpen(true),  []);
  const closeCloseModal = useCallback(() => setIsCloseModalOpen(false), []);

  const value: AppState = {
    currentUser,
    setCurrentUser,
    navConfig: navConfigOverride ?? crowdMonitoringNav(activeAlertCount),
    setNavConfig: config => setNavConfigOverride(config),
    activeOperations,
    selectedOperation,
    operationsResolved,
    setSelectedOperation,
    addActiveOperation,
    removeActiveOperation,
    refreshOperations,
    activeAlertCount,
    setActiveAlertCount: setAlertCnt,
    selectedOperationAlerts,
    selectedOperationAlertSummary,
    alertStreamConnected: operationAlertState.connected,
    alertStreamConnectionStatus: operationAlertState.connectionStatus,
    alertStreamSource: operationAlertState.source,
    alertStreamLastEventAt: operationAlertState.lastEventAt,
    alertStreamLastSyncAt: operationAlertState.lastSyncAt,
    alertStreamError: operationAlertState.error,
    acknowledgeAlert: operationAlertState.acknowledge,
    escalateAlert: operationAlertState.escalate,
    resolveAlert: operationAlertState.resolve,
    theme,
    toggleTheme: () => setTheme(t => (t === 'dark' ? 'light' : 'dark')),
    isOperationModalOpen,
    operationModalVenueId,
    openOperationModal,
    closeOperationModal,
    isCloseModalOpen,
    openCloseModal,
    closeCloseModal,
    logout,
  };

  return <AppCtx.Provider value={value}>{children}</AppCtx.Provider>;
}
